import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Badge, Spinner, PageSkeleton } from '@daycal/ui';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/apiClient.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { FaWhatsapp, FaCheck } from 'react-icons/fa';

// MVP plan upgrade = MANUAL payment (architecture.md §13, §24 M6). The admin configures
// UPI / bank / QR / WhatsApp in Payment Config; the user pays (QR is primary), sends a
// screenshot on WhatsApp, and the admin upgrades the plan by hand (Manage Users).
export default function UpgradePlan() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ['plans'], queryFn: api.listPlans });
  const { data: payData } = useQuery({ queryKey: ['payment-info'], queryFn: api.getPaymentInfo });
  const [selected, setSelected] = useState(null);

  if (isLoading) return <PageSkeleton />;
  const plans = data?.plans || [];
  const pay = payData?.payment_info || {};

  return (
    <div>
      <h1 className="mb-6 text-2xl font-semibold">{t('plans.chooseYourPlan')}</h1>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 items-stretch">
        {plans.map((p) => {
          const isCurrent = p.id === profile?.plan_id;
          return (
            <Card key={p.id} featured={p.is_featured_dark} className="h-full flex flex-col justify-between">
              <div>
                {p.badge_tag && <Badge tone="brand" className="mb-3">{p.badge_tag}</Badge>}
                <h3 className="text-lg font-bold">{p.name}</h3>
                <div className="my-3 text-3xl font-black text-slate-900 dark:text-white">
                  {p.price_display}
                  <span className={`ml-1 text-xs font-normal tracking-wide uppercase ${p.is_featured_dark ? 'text-indigo-300' : 'text-slate-500'}`}>
                    {p.billing_cycle}
                  </span>
                </div>
                <p className={`mb-5 text-sm leading-relaxed ${p.is_featured_dark ? 'text-slate-300' : 'text-slate-500'}`}>{p.description}</p>
                
                <hr className={`my-4 ${p.is_featured_dark ? 'border-indigo-500/30' : 'border-slate-100'}`} />

                <ul className="mb-6 mt-4 space-y-3.5 text-sm">
                  {(p.features_list || []).map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className={`mt-0.5 shrink-0 rounded-full p-1 ${p.is_featured_dark ? 'bg-indigo-500/20 text-indigo-400' : 'bg-emerald-100 text-emerald-600'}`}>
                        <FaCheck className="h-2.5 w-2.5" />
                      </span>
                      <span className={p.is_featured_dark ? 'text-slate-300 font-medium' : 'text-slate-600 font-medium'}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Button
                variant={p.is_featured_dark ? 'secondary' : 'primary'}
                className="w-full mt-auto"
                disabled={isCurrent}
                onClick={() => setSelected(p)}
              >
                {isCurrent ? 'Current plan' : (p.button_text || 'Upgrade')}
              </Button>
            </Card>
          );
        })}
      </div>

      {selected && <PaymentModal plan={selected} pay={pay} onClose={() => setSelected(null)} />}
    </div>
  );
}

function PaymentModal({ plan, pay, onClose }) {
  const [copied, setCopied] = useState(false);

  // Available methods → tabs (QR first, so it's the big primary method).
  const tabs = useMemo(() => {
    const out = [];
    if (pay.payment_qr_url) out.push({ key: 'qr', label: 'QR Code' });
    if (pay.upi_id) out.push({ key: 'upi', label: 'UPI ID' });
    if (pay.bank_details) out.push({ key: 'bank', label: 'Bank Transfer' });
    return out;
  }, [pay]);

  const [tab, setTab] = useState(tabs[0]?.key);
  const active = tabs.some((x) => x.key === tab) ? tab : tabs[0]?.key;

  // Lock background scroll + close on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  const copyUpi = async () => {
    try { await navigator.clipboard.writeText(pay.upi_id); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard blocked */ }
  };

  const waNumber = (pay.whatsapp_number || '').replace(/[^\d]/g, '');
  const waMessage = `Hi, I've paid for the ${plan.name} plan (${plan.price_display}). Here's my payment screenshot.`;
  const waLink = `https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}`;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-slate-100 p-5">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Upgrade to {plan.name}</h2>
            <p className="text-sm text-slate-500">{plan.price_display} {plan.billing_cycle}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Close">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="p-5">
          {/* Instructions */}
          <ol className="mb-4 space-y-1.5 text-sm text-slate-600">
            <li><span className="font-semibold text-slate-900">1.</span> Pay {plan.price_display} using a method below.</li>
            <li><span className="font-semibold text-slate-900">2.</span> Take a screenshot of the successful payment.</li>
            <li><span className="font-semibold text-slate-900">3.</span> Send it to us on WhatsApp — your plan is upgraded after we verify.</li>
          </ol>

          {tabs.length === 0 ? (
            <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-700">Payment details aren’t configured yet. Please contact the administrator on WhatsApp.</p>
          ) : (
            <>
              {/* Tabs */}
              <div className="mb-4 flex gap-1 rounded-lg bg-slate-100 p-1">
                {tabs.map((tb) => (
                  <button
                    key={tb.key}
                    type="button"
                    onClick={() => setTab(tb.key)}
                    className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${active === tb.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    {tb.label}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              {active === 'qr' && (
                <div className="flex flex-col items-center">
                  <img src={pay.payment_qr_url} alt="Payment QR code" className="h-64 w-64 rounded-xl border border-slate-200 object-contain bg-white p-2" />
                  <p className="mt-2 text-sm text-slate-500">Scan with any UPI app to pay {plan.price_display}</p>
                  {pay.upi_id && <p className="mt-1 font-mono text-sm text-slate-700">{pay.upi_id}</p>}
                </div>
              )}

              {active === 'upi' && (
                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-400">UPI ID</div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="font-mono text-lg font-semibold text-slate-900">{pay.upi_id}</span>
                    <Button variant="secondary" onClick={copyUpi}>{copied ? 'Copied ✓' : 'Copy'}</Button>
                  </div>
                  <p className="mt-2 text-sm text-slate-500">Send {plan.price_display} to this UPI ID.</p>
                </div>
              )}

              {active === 'bank' && (
                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">Bank transfer details</div>
                  <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700">{pay.bank_details}</pre>
                </div>
              )}
            </>
          )}

          {/* WhatsApp CTA */}
          {waNumber ? (
            <a href={waLink} target="_blank" rel="noreferrer" className="mt-5 block">
              <Button className="w-full bg-green-600 hover:bg-green-700">
                <FaWhatsapp className="h-5 w-5" />
                Send screenshot on WhatsApp
              </Button>
            </a>
          ) : (
            <p className="mt-5 text-center text-xs text-slate-400">WhatsApp number not configured — contact the administrator.</p>
          )}
          <p className="mt-2 text-center text-xs text-slate-400">Opens WhatsApp with a pre-filled message — attach your screenshot there.</p>
        </div>
      </div>
    </div>,
    document.body
  );
}
